/**
 * Page /grc/dossiers-associations — instruction des dossiers d'association.
 *
 * Pôle GRC/communication (agent lecture, manager/adjoint/maire valident), au même
 * endroit que les dossiers scolaires. Configuration des pièces demandées + file des
 * dossiers + validation. Réutilise AssociationDossierManager (validation + pièces).
 */

import { useEffect, useState } from 'react';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { GrcLayout } from '@/components/grc/GrcLayout';
import { Card, Button, Badge, Alert, LogoMark, SectionEyebrow, Field, Input } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { useToast } from '@/components/ui/Toast';
import { Users2, AlertCircle, Inbox, Settings, Trash2, Plus } from 'lucide-react';
import { AssociationDossierManager } from '../associations/AssociationDossierManager';
import type { AssociationDossier, AssociationDossierStatut, AssociationPieceDemandee } from '@roronoa-collectivites/shared-types';

const BUCKETS: { key: AssociationDossierStatut | 'tous'; label: string }[] = [
  { key: 'soumis', label: 'À traiter' },
  { key: 'valide', label: 'Validés' },
  { key: 'refuse', label: 'Refusés' },
  { key: 'tous', label: 'Tous' },
];

function frDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
}
function statutBadge(s: AssociationDossierStatut) {
  if (s === 'valide') return <Badge variant="success">Validé</Badge>;
  if (s === 'soumis') return <Badge variant="warning">À traiter</Badge>;
  if (s === 'refuse') return <Badge variant="urgent">Refusé</Badge>;
  return <Badge variant="info">Brouillon</Badge>;
}

export function GrcDossiersAssociationsPage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const peutConfigurer = user ? ['super_admin', 'admin', 'manager'].includes(user.role) : false;

  const [items, setItems] = useState<AssociationDossier[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bucket, setBucket] = useState<AssociationDossierStatut | 'tous'>('soumis');

  // Dialog validation
  const [cible, setCible] = useState<{ id: string; nom: string } | null>(null);

  // Config pièces demandées
  const [configOpen, setConfigOpen] = useState(false);
  const [catalogue, setCatalogue] = useState<AssociationPieceDemandee[]>([]);
  const [nouvellePiece, setNouvellePiece] = useState('');
  const [pieceObligatoire, setPieceObligatoire] = useState(true);

  async function charger() {
    setLoading(true); setError(null);
    try {
      setItems(await api.associations.dossiers.list(bucket === 'tous' ? undefined : bucket));
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Erreur de chargement');
    } finally { setLoading(false); }
  }
  useEffect(() => { charger(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [bucket]);

  async function ouvrirConfig() {
    setConfigOpen(true);
    try { setCatalogue(await api.associations.dossiers.listPiecesDemandees()); } catch { setCatalogue([]); }
  }
  async function ajouterPiece() {
    if (!nouvellePiece.trim()) return;
    try {
      await api.associations.dossiers.createPieceDemandee(nouvellePiece.trim(), pieceObligatoire);
      setNouvellePiece('');
      setCatalogue(await api.associations.dossiers.listPiecesDemandees());
    } catch (e) { toast.error(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function supprimerPiece(id: string) {
    try {
      await api.associations.dossiers.deletePieceDemandee(id);
      setCatalogue(await api.associations.dossiers.listPiecesDemandees());
    } catch (e) { toast.error(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  return (
    <GrcLayout active="dossiers">
      <main id="main-content" className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-10 animate-fade-in">
        <div className="mb-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <LogoMark icon={Users2} accent="grc" size="lg" />
            <div>
              <SectionEyebrow as="div">Vie associative</SectionEyebrow>
              <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">Dossiers associations</h1>
            </div>
          </div>
          {peutConfigurer && (
            <Button variant="secondary" size="sm" onClick={ouvrirConfig}>
              <Settings className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">Pièces demandées</span>
            </Button>
          )}
        </div>

        {error && <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>}

        <div className="mb-4 flex flex-wrap gap-1.5">
          {BUCKETS.map((b) => (
            <button key={b.key} type="button" onClick={() => setBucket(b.key)}
              className={['inline-flex h-9 items-center rounded-r-pill px-4 text-sm font-medium transition-all',
                bucket === b.key ? 'bg-grc text-white shadow-soft' : 'text-ink-muted hover:bg-surface/70'].join(' ')}>
              {b.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-sm text-ink-muted">Chargement…</p>
        ) : !items || items.length === 0 ? (
          <Card>
            <div className="flex flex-col items-center gap-3 p-8 text-center">
              <Inbox className="h-8 w-8 text-ink-subtle" />
              <div className="text-sm text-ink-muted">Aucun dossier dans cette catégorie.</div>
            </div>
          </Card>
        ) : (
          <div className="space-y-2">
            {items.map((d) => (
              <Card key={d.id}>
                <button type="button" onClick={() => setCible({ id: d.associationId, nom: d.associationNom ?? 'Association' })}
                  className="flex w-full items-center justify-between gap-3 p-3 text-left transition-colors hover:bg-surface/60">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-ink">{d.associationNom}</div>
                    <div className="text-xs text-ink-subtle">
                      {d.soumisAt ? `Soumis le ${frDate(d.soumisAt)}` : 'Non soumis'}
                    </div>
                  </div>
                  {statutBadge(d.statut)}
                </button>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Dialog validation (réutilise le manager) */}
      <Dialog open={!!cible} onClose={() => { setCible(null); charger(); }} title={cible ? `Dossier — ${cible.nom}` : 'Dossier'} size="lg">
        {cible && <AssociationDossierManager associationId={cible.id} />}
      </Dialog>

      {/* Dialog config pièces demandées */}
      <Dialog open={configOpen} onClose={() => setConfigOpen(false)} title="Pièces demandées aux associations" size="md">
        <div className="space-y-4">
          <p className="text-sm text-ink-muted">
            Documents que les associations doivent fournir. Cette liste s'applique à toute la commune.
          </p>
          <div className="space-y-2">
            {catalogue.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2 rounded-r-md border border-border bg-surface/40 p-2.5">
                <div className="flex items-center gap-2 text-sm text-ink">
                  {p.libelle}{p.obligatoire && <Badge variant="info">obligatoire</Badge>}
                </div>
                <Button variant="ghost" size="sm" onClick={() => supprimerPiece(p.id)} className="text-urgent hover:bg-urgent/10 hover:text-urgent">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
            {catalogue.length === 0 && <div className="text-sm text-ink-subtle">Aucune pièce demandée.</div>}
          </div>
          <div className="border-t border-border pt-3">
            <Field label="Ajouter une pièce" htmlFor="grc-np">
              <Input id="grc-np" value={nouvellePiece} onChange={(e) => setNouvellePiece(e.target.value)} placeholder="Ex. Liste des membres du bureau" />
            </Field>
            <label className="mt-2 flex items-center gap-2 text-sm text-ink-muted">
              <input type="checkbox" checked={pieceObligatoire} onChange={(e) => setPieceObligatoire(e.target.checked)} /> Pièce obligatoire
            </label>
            <div className="mt-2 flex justify-end">
              <Button variant="primary" size="sm" onClick={ajouterPiece}><Plus className="h-4 w-4" /> Ajouter</Button>
            </div>
          </div>
        </div>
      </Dialog>
    </GrcLayout>
  );
}
